import type { LocalDatabase } from './types';

import { reindexBlockSearchCascade } from './search-index';
import type { BlockRecord } from '../blocks/types';

function isoToMillis(iso: string | undefined | null, fallbackMs: number): number {
  if (iso == null || iso === '') {
    return fallbackMs;
  }
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : fallbackMs;
}

type BlockRow = {
  id: string;
  parent_id: string | null;
  position: number;
  type: string;
  title: string | null;
  content: string;
  icon: string | null;
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
  created_by: string | null;
  updated_by: string | null;
  actor_kind: string;
  vector_clock: string;
  sync_version: number;
};

function rowToBlockRecord(row: BlockRow): BlockRecord {
  return {
    id: row.id,
    parentId: row.parent_id,
    position: row.position,
    type: row.type,
    title: row.title,
    content: JSON.parse(row.content) as Record<string, unknown>,
    icon: row.icon,
    createdAt: new Date(row.created_at).toISOString(),
    updatedAt: new Date(row.updated_at).toISOString(),
    deletedAt: row.deleted_at ? new Date(row.deleted_at).toISOString() : null,
    createdBy: row.created_by,
    updatedBy: row.updated_by,
    actorKind: row.actor_kind,
    vectorClock: JSON.parse(row.vector_clock) as Record<string, number>,
    syncVersion: row.sync_version,
  };
}

export async function queryBlockTree(
  database: LocalDatabase,
  parentId: string | null,
): Promise<BlockRecord[]> {
  const rows = parentId
    ? await database.getAllAsync<BlockRow>(
        `SELECT * FROM blocks WHERE parent_id = ? AND deleted_at IS NULL ORDER BY position ASC`,
        parentId,
      )
    : await database.getAllAsync<BlockRow>(
        `SELECT * FROM blocks WHERE parent_id IS NULL AND deleted_at IS NULL ORDER BY position ASC`,
      );
  return rows.map(rowToBlockRecord);
}

const NAV_COLUMNS = `id, parent_id, position, type, title, icon, created_by`;

type NavBlockRow = {
  id: string;
  parent_id: string | null;
  position: number;
  type: string;
  title: string | null;
  icon: string | null;
  created_by: string | null;
};

function navRowToBlock(row: NavBlockRow): BlockRecord {
  return {
    id: row.id,
    parentId: row.parent_id,
    position: row.position,
    type: row.type,
    title: row.title,
    content: {},
    icon: row.icon,
    createdAt: '',
    updatedAt: '',
    deletedAt: null,
    createdBy: row.created_by,
    updatedBy: null,
    actorKind: 'human',
    vectorClock: {},
    syncVersion: 0,
  };
}

function parseBlockContent(raw: string | null): Record<string, unknown> {
  if (!raw) {
    return {};
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // Root metadata only; ignore malformed content.
  }
  return {};
}

/** Page and database rows for the sidebar, without body-block content. */
export async function queryNavBlocks(database: LocalDatabase): Promise<BlockRecord[]> {
  const rows = await database.getAllAsync<NavBlockRow>(
    `SELECT ${NAV_COLUMNS} FROM blocks
     WHERE deleted_at IS NULL AND type IN ('page', 'database')
     ORDER BY position ASC`,
  );
  const blocks = rows.map(navRowToBlock);
  const rootContent = await database.getAllAsync<{ id: string; content: string | null }>(
    `SELECT id, content FROM blocks
     WHERE parent_id IS NULL AND deleted_at IS NULL AND type = 'page'`,
  );
  if (rootContent.length === 0) {
    return blocks;
  }
  const contentById = new Map(rootContent.map((row) => [row.id, parseBlockContent(row.content)]));
  return blocks.map((block) => {
    const content = contentById.get(block.id);
    return content ? { ...block, content } : block;
  });
}

export async function queryNavChildren(
  database: LocalDatabase,
  parentId: string,
): Promise<BlockRecord[]> {
  const rows = await database.getAllAsync<NavBlockRow>(
    `SELECT ${NAV_COLUMNS} FROM blocks
     WHERE parent_id = ? AND deleted_at IS NULL AND type IN ('page', 'database')
     ORDER BY position ASC`,
    parentId,
  );
  return rows.map(navRowToBlock);
}

export async function queryBlock(database: LocalDatabase, id: string): Promise<BlockRecord | null> {
  const row = await database.getFirstAsync<BlockRow>(
    `SELECT * FROM blocks WHERE id = ? AND deleted_at IS NULL`,
    id,
  );
  return row ? rowToBlockRecord(row) : null;
}

export async function queryPageContentBlocks(
  database: LocalDatabase,
  pageId: string,
): Promise<BlockRecord[]> {
  const contentBlocks: BlockRecord[] = [];
  let frontier = [pageId];

  while (frontier.length > 0) {
    const placeholders = frontier.map(() => '?').join(', ');
    const rows = await database.getAllAsync<BlockRow>(
      `SELECT * FROM blocks WHERE parent_id IN (${placeholders}) AND deleted_at IS NULL ORDER BY position ASC`,
      ...frontier,
    );

    const nextFrontier: string[] = [];
    for (const row of rows) {
      if (row.type === 'page' || row.type === 'database') {
        continue;
      }
      contentBlocks.push(rowToBlockRecord(row));
      nextFrontier.push(row.id);
    }
    frontier = nextFrontier;
  }

  return contentBlocks;
}

export async function upsertBlockLocal(
  database: LocalDatabase,
  block: BlockRecord,
): Promise<void> {
  const now = Date.now();
  const createdMs = isoToMillis(block.createdAt, now);
  const updatedMs = isoToMillis(block.updatedAt, now);
  await database.runAsync(
    `INSERT OR REPLACE INTO blocks
      (id, parent_id, position, type, title, content, icon, created_at, updated_at, deleted_at,
       created_by, updated_by, actor_kind, vector_clock, sync_version)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    block.id,
    block.parentId,
    block.position,
    block.type,
    block.title,
    JSON.stringify(block.content),
    block.icon,
    createdMs,
    updatedMs,
    block.deletedAt ? isoToMillis(block.deletedAt, now) : null,
    block.createdBy,
    block.updatedBy,
    block.actorKind,
    JSON.stringify(block.vectorClock),
    block.syncVersion,
  );
  await reindexBlockSearchCascade(database, block.id);
}

export async function softDeleteBlockLocal(database: LocalDatabase, id: string): Promise<void> {
  const now = Date.now();
  await database.runAsync(
    `UPDATE blocks SET deleted_at = ?, updated_at = ? WHERE id = ?`,
    now,
    now,
    id,
  );
  await reindexBlockSearchCascade(database, id);
}

export async function collectSubtreeBlockIdsLocal(
  database: LocalDatabase,
  rootId: string,
): Promise<string[]> {
  const collected: string[] = [];
  let frontier = [rootId];

  while (frontier.length > 0) {
    const placeholders = frontier.map(() => '?').join(', ');
    const rows = await database.getAllAsync<{ id: string }>(
      `SELECT id FROM blocks WHERE parent_id IN (${placeholders}) AND deleted_at IS NULL`,
      ...frontier,
    );
    const ids = rows.map((row) => row.id);
    collected.push(...ids);
    frontier = ids;
  }

  return collected;
}

export async function collectAllSubtreeBlockIdsLocal(
  database: LocalDatabase,
  rootId: string,
): Promise<string[]> {
  const collected: string[] = [];
  let frontier = [rootId];

  while (frontier.length > 0) {
    const placeholders = frontier.map(() => '?').join(', ');
    const rows = await database.getAllAsync<{ id: string }>(
      `SELECT id FROM blocks WHERE parent_id IN (${placeholders})`,
      ...frontier,
    );
    const ids = rows.map((row) => row.id);
    collected.push(...ids);
    frontier = ids;
  }

  return collected;
}

export async function queryBlockIncludingDeleted(
  database: LocalDatabase,
  id: string,
): Promise<BlockRecord | null> {
  const row = await database.getFirstAsync<BlockRow>(`SELECT * FROM blocks WHERE id = ?`, id);
  return row ? rowToBlockRecord(row) : null;
}

export async function updateBlockSyncVersion(
  database: LocalDatabase,
  blockId: string,
  syncVersion: number,
): Promise<void> {
  await database.runAsync(`UPDATE blocks SET sync_version = ? WHERE id = ?`, syncVersion, blockId);
}

const SYNC_VERSION_TABLE: Record<string, { table: string; idColumn: string }> = {
  blocks: { table: 'blocks', idColumn: 'id' },
  block_properties: { table: 'block_properties', idColumn: 'id' },
  databases: { table: 'databases', idColumn: 'id' },
  database_schemas: { table: 'database_schemas', idColumn: 'id' },
  relations: { table: 'relations', idColumn: 'id' },
  members: { table: 'members', idColumn: 'id' },
  block_acl: { table: 'block_acl', idColumn: 'id' },
  file_objects: { table: 'file_objects', idColumn: 'id' },
};

export async function updateRowSyncVersion(
  database: LocalDatabase,
  tableName: string,
  rowId: string,
  syncVersion: number,
): Promise<void> {
  const spec = SYNC_VERSION_TABLE[tableName];
  if (!spec) {
    return;
  }
  await database.runAsync(
    `UPDATE ${spec.table} SET sync_version = ? WHERE ${spec.idColumn} = ?`,
    syncVersion,
    rowId,
  );
}
